import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { matchBand } from '../../utils/pathwayMatching';
interface ModuleCardProps {
  match: MatchedModule;
  inPathway: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onTogglePathway: () => void;
}
const bandStyles: Record<string, string> = {
  Strong: 'border-[#05634B] text-[#007558]',
  Good: 'border-[#5290A6] text-[#30718D]',
  Partial: 'border-[#828282] text-[#6B7280]'
};
export function ModuleCard({
  match,
  inPathway,
  expanded,
  onToggleExpand,
  onTogglePathway
}: ModuleCardProps) {
  const {
    module,
    score,
    matchedTerms
  } = match;
  const band = score !== null ? matchBand(score) : null;
  const detailsId = `module-details-${module.id}`;
  return <article className={`bg-white rounded-xl shadow-sm border transition-colors ${inPathway ? 'border-[#30718D]' : 'border-gray-100'}`} data-id="element-1549" data-mp="3jhc77njdd9un">
      <div className="p-5" data-id="element-1550" data-mp="38divkcpjxdcv">
        <div className="flex items-start gap-4" data-id="element-1551" data-mp="38gwv7d6tz8gm">
          <button type="button" onClick={onTogglePathway} aria-pressed={inPathway} aria-label={inPathway ? `Remove ${module.name} from pathway` : `Add ${module.name} to pathway`} className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] ${inPathway ? 'border-[#30718D] bg-[#30718D] text-white hover:bg-[#265a70]' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-1552" data-mp="33fhc3qo7rc0v">
            {inPathway ? <Check size={16} data-id="element-1553" data-mp="dxqr89gxqvql" /> : <Plus size={16} data-id="element-1554" data-mp="dy9ymw8ym1qd" />}
          </button>

          <div className="min-w-0 flex-1" data-id="element-1555" data-mp="8zyoeker5p7m">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-1556" data-mp="908es93plydu">
              <h3 className="text-base font-bold text-gray-900" data-id="element-1557" data-mp="23ew0zq6n3emy">
                {module.name}
              </h3>
              <span className="text-sm text-gray-500" data-id="element-1558" data-mp="287fd0ipoi1pn">{module.category}</span>
            </div>

            <p className="mt-1.5 text-sm text-gray-600" data-id="element-1559" data-mp="1tj2epf34c6rz">{module.description}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1560" data-mp="1yg19lzulwhr8">
              {band && <span className={`inline-flex h-6 items-center justify-center rounded border bg-white px-3 text-xs font-bold whitespace-nowrap ${bandStyles[band]}`} data-id="element-1561" data-mp="2m65ahzgeu3ej">
                  {band} match
                </span>}
              <span className="text-xs text-gray-500" data-id="element-1562" data-mp="2h7bwm5x7oadu">
                {module.levels.map(l => LEVEL_LABELS[l]).join(', ')}
              </span>
            </div>

            {matchedTerms.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1563" data-mp="15rx1l7okbsld">
                <span className="text-xs text-gray-500" data-id="element-1564" data-mp="15q2h1uh7e2ht">Matched:</span>
                {matchedTerms.slice(0, 4).map(term => <span key={term} className="inline-flex rounded bg-[#30718D]/10 px-2 py-1 text-xs font-medium text-[#30718D]" data-id="element-1565" data-mp="3f1cv1h2xmmqe">
                    {term}
                  </span>)}
              </div>}
          </div>

          <button type="button" onClick={onToggleExpand} aria-expanded={expanded} aria-controls={detailsId} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1566" data-mp="318x073k6pv0k">
            {expanded ? 'Less' : 'Details'}
            <ChevronDown size={14} className={`transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} data-id="element-1567" data-mp="2hk4ox9h98ulg" />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1568" data-mp="2q70c7ynbc49b">
        {expanded && <motion.div id={detailsId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-t border-gray-100" data-id="element-1569" data-mp="2v8y0j7aklp94">
            <div className="grid gap-6 p-5 sm:grid-cols-2" data-id="element-1570" data-mp="39zezmjzqg32u">
              <div data-id="element-1571" data-mp="3dxcp7nxf0r6r">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1572" data-mp="3dwupghbjnuu3">
                  Topics
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1573" data-mp="3nxdf5spsi5jx">
                  {module.topics.map(topic => <li key={topic} className="text-sm text-gray-700" data-id="element-1574" data-mp="1ok98jfmvky86">
                      {topic}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-1575" data-mp="1lz1im8fpj0v4">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1576" data-mp="1lzjn1sciwle4">
                  What you’ll learn
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1577" data-mp="1h6i67esrabrw">
                  {module.outcomes.map(outcome => <li key={outcome} className="text-sm text-gray-700" data-id="element-1578" data-mp="1c75iysxz32os">
                      {outcome}
                    </li>)}
                </ul>
              </div>
              <div className="sm:col-span-2 border-t border-gray-100 pt-4" data-id="element-1579" data-mp="24w318w26ozhj">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1580" data-mp="1zzm276tg50po">
                  Why this came up
                </div>
                <p className="mt-1.5 text-sm text-gray-700" data-id="element-1581" data-mp="1q157byre01yc">
                  {module.relevance}
                </p>
                <p className="mt-2 text-xs text-gray-400" data-id="element-1582" data-mp="jl2xlgtyoj6c">
                  Skill Module ID: {module.courseId}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </article>;
}